import { Feature, featureCollection, point } from '@turf/helpers'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { MultiPolygon, Point, Polygon } from 'geojson'
import queryString from 'query-string'
import { assocPath, flatten, pick, pipe, splitEvery } from 'ramda'
import {
  AgreementStatus,
  BenchmarkStatus,
  CollectionSpace,
  Field,
  FieldEligibilityState,
  FieldMetadata,
  MpxListResponse,
  PaginationRequest,
  PaginationResponse,
  Persona,
  ProgramBenchmarkName,
  ProgramReward,
  ResourceDetail,
  ResourceDetailsUpdate,
  ResourceProgramState,
  RewardOpportunity,
  RewardToken,
  SortModel,
  UserId,
} from '../types'
import { MpxResponseBase, MpxResponseBaseSingle } from '../types/MpxTypes'
import { generatePath, labelPositionForGeometry } from '../utils'
import { getMpAgent } from './utils'
import { queueRequest } from './utils/requestQueue'

const mpAgent = getMpAgent()

const PATHS = {
  ASSIGN: '/fields/assign',
  BATCH_DETAILS: '/fields/batchDetails',
  CONFIRM_IMPORT: '/fields/importFromScratch',
  FIELD: '/fields/:resourceId',
  FIELDS_QUERY_SHAPEFILE: '/fields/query/shapefile',
  FIELDS_QUERY: '/fields/query',
  FIELDS: '/fields',
  FORCE_UPDATE: '/fields/:resourceId/forceUpdate',
  GEOJSON: '/fields/query/geojson',
  LOCAL_LEDGER: '/fields/ledgerFieldsInViewport',
  METADATA: '/fields/metadata',
  STATS: '/fields/query/stats',
  UPLOAD: '/fields/:resourceId/detail/file',
}

export const FIELD_QUERY_MAX_LIMIT = 1000

export interface FieldMetadataUpdateRequest {
  metadata: Partial<FieldMetadata>
  resourceIds: string[]
  action?: 'merge' | 'replace' | 'remove' | 'clear'
}

export interface FieldResponse extends MpxResponseBase {
  item: Field
}
export interface FieldsResponse extends MpxResponseBase {
  items: Field[]
}

export type FieldQuerySortFields =
  | 'acres'
  | 'county'
  | 'createdAt'
  | 'updatedAt'
  | 'name'
  | 'programCount'
  | 'programInteractedAt'
  | 'recommendationScore'
  | 'state'

export type ProgramFieldSelector = {
  id: string
  benchmarks?: Array<{
    benchmark: ProgramBenchmarkName
    status: BenchmarkStatus
  }>
  participating?: boolean
  workflow?: boolean
}

export const FDR_FILTER_STATUSES = [
  'needsInfo',
  'ineligibleForEnrollment',
  'eligibleForEnrollment',
  'enrolling',
  'enrolled',
  'completed',
  'contracted',
]

export type FieldBenchmarkStatus = typeof FDR_FILTER_STATUSES[number]

export enum FDRStatus {
  canEngage = 'canEngage',
  agreementComplete = 'agreementComplete',
  agreementPending = 'agreementPending',
  eligibleForEnrollment = 'eligibleForEnrollment',
  contracted = 'contracted',
  completed = 'completed',
  enrolled = 'enrolled',
  enrolling = 'enrolling',
  futureEnrollment = 'futureEnrollment',
  futureProgram = 'futureProgram',
  hasOffer = 'hasOffer',
  hasOpportunity = 'hasOpportunity',
  hasReward = 'hasReward',
  historicalProgram = 'historicalProgram',
  ineligibleForEnrollment = 'ineligibleForEnrollment',
  // not being accepted by backend
  // isEnrolled = 'isEnrolled',
  isEngaged = 'isEngaged',
  needsInfo = 'needsInfo',
  postEnrollNoActionNeeded = 'postEnrollNoActionNeeded',
  profileIncomplete = 'profileIncomplete',
  readyToEnroll = 'readyToEnroll',
  remoteSensingResolved = 'remoteSensingResolved',
  rewardsResolved = 'rewardsResolved',
  ruleIneligible = 'ruleIneligible',
  underReview = 'underReview',
}

export type FDRStatsQueryItem = {
  status: FDRStatus
  programId?: string
  sort?: {
    sortDir: 'asc' | 'desc'
    // path to the value used to sort response items.
    // eg. ["acres"], ["fields"], ["rewards", "opportunities", "usd", "amount"]
    // ["rewards", "opportunities", "carbonCredit", "amount"]
    value: ['fields']
  }
}

export interface FDRStatusEntry {
  status: FDRStatus[]
  programId: string
}

export interface FDRStatusQuery {
  status: FDRStatus
  programId?: string
}

export type OfferSearchParameters = {
  gtePaymentAmount?: number
  ltPaymentAmount?: number
  gteExpirationDate?: string //timestamp
  ltExpirationDate?: string //timestamp
  status?: AgreementStatus
  contracted?: boolean
  offerGroup?: string
}

export interface FieldQueryFilters {
  fips?: string[]
  cashCrop?: string[]
  coverCrop?: string[]
  tillage?: string[]
  acresLt?: number
  acresGt?: number
  labels?: string[]
  lastUpdatedGt?: string
  lastUpdatedLt?: string
  createdGt?: string
  createdLt?: string
  termsets?: string[]
  grower?: string
  owner?: string | UserId // Email address or userId of the owner of the field.
  userId?: UserId
  orgId?: string
}

export type FieldQueryFilterName = keyof FieldQueryFilters

export interface FieldQueryOptions
  extends PaginationRequest<FieldQuerySortFields>,
    FieldQueryFilters {
  resourceIds?: string
  includeAllDetails?: boolean
  includeAllResolved?: boolean
  fullText?: string
  footprintResolved?: boolean
  involvedWithProgram?: boolean
  profiled?: boolean
  space?: CollectionSpace
  programs?: Array<ProgramFieldSelector>
  anyPrograms?: Array<ProgramFieldSelector>
  allPrograms?: Array<ProgramFieldSelector>
  persona?: Persona
  programStateIds?: string[]
  programState?: Array<ResourceProgramState | '!selected' | '!committed' | '!completed' | 'none'>
  programStatusIds?: string[]
  programStatus?: Array<FieldEligibilityState | '!eligible' | '!ineligible' | '!incomplete'>
  programPhaseIds?: string[]
  programPhase?: Array<ProgramBenchmarkName | '!prequalify' | '!enrollment'>
  name?: string

  availableForWorkflow?: {
    programId: string
  }

  workflow?: string // this is a workflow id

  prgGlobalSearchStatus?: Array<FieldBenchmarkStatus>
  prgGlobalSearchStatusProgramId?: string // scope the filter above
  fdrStatusAnyOf?: FDRStatusQuery[]
  fdrStatusAllOf?: FDRStatusQuery[]

  agreementId?: string
  searchOption?: 'all' | 'syndicating' | 'nonSyndicating'

  offerSearch?: OfferSearchParameters
}

/**
 * ugh
 * Why this doesn't extend RewardOpportunity is beyond me.  This
 * response resembles a RewardOpportunity but is... different
 */
export interface FieldFDRStats extends Pick<RewardOpportunity, 'acres'> {
  programId: string
  status: FDRStatus
  fields?: RewardOpportunity['numResources'] // yep!!
  // I can't even with this...
  rewards?: {
    opportunities: Record<RewardToken, Pick<ProgramReward, 'amount' | 'token'>>
  }
  personaCounts?: Record<Persona, { fields: number }>
}

export interface FieldFDRStatsResponse extends MpxResponseBase {
  fdrStats: FieldFDRStats[]
}

export interface FieldsStatsQueryOptions extends FieldQueryOptions {
  fdrStats?: Array<FDRStatusQuery>
}

export interface FieldGeoJSONQueryOptions extends Omit<FieldQueryOptions, 'resourceIds'> {
  lowerLeft: [number, number]
  upperRight: [number, number]
  workflow?: string
  owner?: string | UserId // Email address or userId of the owner of the field.
}

export interface LocalLedgerFieldsRequest {
  lowerLeft: [number, number]
  upperRight: [number, number]
}

export interface FieldShapefilesQueryOptions
  extends Omit<FieldQueryOptions, 'offset' | 'limit' | 'sort'> {
  //Choice of mapping of field data to metadata for the DBF (DBase 3 format) file.
  dbfMappingChoice?: 'defaultOrgMapping' | 'standard'
  //The base name of the 'save as' file name returned in the response. If it is not provided, a name will be generated.
  fileName?: string
}

export interface AssignFieldsRequestParams {
  userId: UserId
  resourceIds: string[]
  messageUser: boolean
  customMessage?: string
}

type CreateFieldRequest = {
  details: ResourceDetail[]
  owner?: string
  metadata: any
  space?: CollectionSpace
}

type ResourceModification = {
  resourceId: string
  name?: string
  labels?: string[]
}

export type ConfirmImportsRequest = {
  resourceIds: string[]
  owner?: UserId
  resourceModifications?: ResourceModification[]
}

const pickStatus = (statuses?: string) => {
  if (!statuses) return
  if (statuses.includes('enrolled')) return 'enrolled'
  if (statuses.includes('qualified')) return 'qualified'
  return statuses[0]
}

type FieldMapFeature = Feature<Point | Polygon | MultiPolygon, any>

export class FieldsAPI {
  static ErrorMessages = {
    DUPLICATE_FIELD_NAME:
      'Failed to preprocess inputs for trait name Found field with duplicate name in field group.',
  }

  static assign(request: AssignFieldsRequestParams) {
    return mpAgent.patch(PATHS.ASSIGN, request)
  }

  static create(request: CreateFieldRequest) {
    return mpAgent
      .post<MpxResponseBaseSingle<Field>>(generatePath(PATHS.FIELDS), {
        ...pick(['details', 'metadata', 'owner'], request),
        targetCollectionId: request.space,
      })
      .then(response => response && response.data)
  }

  static bulkUpdateFields(resourceIds: string[], details: ResourceDetail[]) {
    return mpAgent
      .patch<MpxListResponse<Field>>(generatePath(PATHS.FIELDS), {
        resourceIds,
        details,
      })
      .then(response => response && response.data)
  }

  static async fields(resourceIds?: string[], queryOptions?: FieldQueryOptions) {
    const { sort, ...otherParams } = queryOptions || {}

    const getBatch = (batchParams?: FieldQueryOptions) =>
      mpAgent
        .get<FieldsResponse>(PATHS.FIELDS, {
          params: {
            ...batchParams,
            ...otherParams,
            searchOption: 'all',
            sort: !!sort ? sort.map(({ sort, field }) => `${sort}:${field}`).join() : undefined,
          },
          paramsSerializer: p => queryString.stringify(p),
        })
        .then(response => response && response.data.items)

    if (!resourceIds) {
      return getBatch(queryOptions)
    }

    const batches = splitEvery(25, resourceIds)

    //@ts-ignore
    const responses: Field[][] = await Promise.all(
      batches.map(batchIds => getBatch({ resourceIds: batchIds.join(','), ...queryOptions }))
    )

    return flatten(responses)
  }

  static paginatedFields(params?: FieldQueryOptions, config?: AxiosRequestConfig) {
    return mpAgent
      .post<PaginationResponse<Field, FieldQueryOptions>>(
        PATHS.FIELDS_QUERY,
        FieldsAPI.transformFieldQueryParams(
          params ? { searchOption: 'all', ...params } : { searchOption: 'all' }
        ),
        config
      )
      .then(
        response =>
          response && {
            ...response.data,
            params,
          }
      )
  }

  /**
   * similar to, but not exactly like `pagintedFields`.  Specifically
   * this endpoint doesn't support pagination
   */
  static shapefiles(params: FieldShapefilesQueryOptions, config?: AxiosRequestConfig) {
    const {
      grower,
      programs = [],
      owner,
      prgGlobalSearchStatus,
      prgGlobalSearchStatusProgramId,
      ...passthroughParams
    } = params || {}

    return mpAgent.post<any>(
      PATHS.FIELDS_QUERY_SHAPEFILE,
      {
        ...passthroughParams,
        owner: !!owner ? `${owner}` : undefined,
        grower: grower && grower !== '' ? grower : undefined,
        programs: programs.length === 0 ? undefined : programs,
        prgGlobalSearchStatus: !!prgGlobalSearchStatus?.length
          ? prgGlobalSearchStatus.join(',')
          : undefined,
        prgGlobalSearchStatusProgramId,
        // paramsSerializer: p => queryString.stringify(p),
      },
      config
    )
  }

  static localLedgerFields({ lowerLeft, upperRight }: LocalLedgerFieldsRequest) {
    return mpAgent
      .get<PaginationResponse<FieldMapFeature>>(PATHS.LOCAL_LEDGER, {
        params: {
          lowerLeft: lowerLeft?.join(),
          upperRight: upperRight?.join(),
        },
      })
      .then(response => {
        if (!!response) {
          const items = response.data.items.map(
            feature =>
              pipe(
                // workaround until CPD-4902
                assocPath(
                  ['boundingBox'],
                  feature?.properties?.bbox.split(',').map((coord: string) => parseFloat(coord))
                )
              )(feature) as FieldMapFeature
          )

          return featureCollection(items)
        }
        return null
      })
  }

  static geoJSONSearch(
    params?: Omit<FieldGeoJSONQueryOptions, 'resourceIds'>,
    skipPointVersions = false
  ) {
    const {
      sort,
      grower,
      programState,
      programStatus,
      programPhase,
      lowerLeft,
      upperRight,
      prgGlobalSearchStatus,
      prgGlobalSearchStatusProgramId,
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // @ts-ignore geojson endpoint does not accept this
      resourceIds,
      /* eslint-enable */
      ...passthroughParams
    } = params || {}

    return mpAgent
      .post<PaginationResponse<FieldMapFeature>>(PATHS.GEOJSON, {
        ...passthroughParams,
        grower: grower && grower !== '' ? grower : undefined,
        programPhase: !!programPhase ? programPhase.join() : undefined,
        programState: !!programState ? programState.join() : undefined,
        programStatus: !!programStatus ? programStatus.join() : undefined,
        sort: FieldsAPI.transformQuerySortParam(sort),
        lowerLeft: lowerLeft?.join(),
        upperRight: upperRight?.join(),
        prgGlobalSearchStatus: !!prgGlobalSearchStatus?.length
          ? prgGlobalSearchStatus.join(',')
          : undefined,
        prgGlobalSearchStatusProgramId,
      })
      .then(response => {
        if (!!response) {
          const items = response.data.items.map(
            feature =>
              pipe(
                assocPath(
                  ['properties', 'status'],
                  pickStatus(feature?.properties?.prgGlobalSearchStatuses)
                ),
                // workaround until CPD-4902
                assocPath(
                  ['boundingBox'],
                  feature?.properties?.bbox.split(',').map((coord: string) => parseFloat(coord))
                )
              )(feature) as FieldMapFeature
          )

          if (skipPointVersions) {
            return featureCollection(items)
          }

          const withPointFeatures: FieldMapFeature[] = []

          items.forEach((item: FieldMapFeature) => {
            withPointFeatures.push(item)

            if (item.geometry.type !== 'Point') {
              const position = labelPositionForGeometry(item.geometry)
              const label = point(position as number[], item.properties)
              withPointFeatures.push(label)
            }
          })

          return featureCollection(withPointFeatures)
        }
        return null
      })
  }

  // This should only be used to fetch the field count (and acreage) of
  static getMultipleFieldQueries = async (
    queries: FieldQueryOptions[],
    queryKey: keyof FieldQueryOptions
  ) => ({
    results: await Promise.all(
      queries.map(query =>
        FieldsAPI.paginatedFields(query).then(response => ({
          queryKey: query[queryKey],
          ...response,
        }))
      )
    ),
  })

  static field(resourceId: string, options?: any) {
    return mpAgent
      .get<FieldResponse>(generatePath(PATHS.FIELD, { resourceId }), options)
      .then(response => response && response.data.item)
  }

  static deleteField(resourceId: string) {
    return mpAgent.delete<MpxResponseBase>(generatePath(PATHS.FIELD, { resourceId }))
  }

  static deleteFields(resourceIds: string[]) {
    return mpAgent.delete<MpxResponseBase>(generatePath(PATHS.FIELDS), {
      data: { resourceIds: resourceIds.join() },
      params: { deleteWfOnEmpty: 'true' },
    })
  }

  static forceCommitField(resourceId: string, programId: string) {
    return mpAgent.put<FieldResponse>(generatePath(PATHS.FORCE_UPDATE, { resourceId }), {
      prgIdToEnroll: programId,
    })
  }

  static unenrollField(resourceId: string, programId: string) {
    return mpAgent.put<FieldResponse>(generatePath(PATHS.FORCE_UPDATE, { resourceId }), {
      prgIdToUnenroll: programId,
    })
  }

  // This is a Partial detail because sending no input means DELETE
  static updateField(resourceId: string, details: Array<Partial<ResourceDetail>>) {
    return queueRequest<AxiosResponse<MpxResponseBaseSingle<Field>>>(() =>
      mpAgent.patch<MpxResponseBaseSingle<Field>>(
        { template: PATHS.FIELD, params: { resourceId } },
        {
          details,
        }
      )
    )
  }

  static updateMultipleFields(batchDetails: ResourceDetailsUpdate[]) {
    return queueRequest(() =>
      mpAgent.patch<MpxListResponse<Field>>(generatePath(PATHS.BATCH_DETAILS), {
        batchDetails,
      })
    )
  }

  static updateMetadata(payload: FieldMetadataUpdateRequest) {
    return mpAgent.patch(generatePath(PATHS.METADATA), payload)
  }

  static uploadFileDetail(resourceId: string, payload: FormData, config?: AxiosRequestConfig) {
    return mpAgent.put<FieldResponse>(generatePath(PATHS.UPLOAD, { resourceId }), payload, config)
  }

  static deleteFileDetailAndRelatedDetails(resourceId: string, traitId: string, year?: number) {
    return mpAgent.delete<FieldResponse>(generatePath(PATHS.UPLOAD, { resourceId }), {
      params: { traitId, year },
    })
  }

  private static transformQuerySortParam(sort: SortModel<FieldQuerySortFields> | undefined) {
    return !!sort
      ? sort
          .map(({ sort, field }) => {
            if (field.startsWith('recommendationScore:')) {
              return `${field.split(':')[0]}:${sort}`
            }
            return `${field}:${sort}`
          })
          .join()
      : undefined
  }

  static confirmImports(request: ConfirmImportsRequest) {
    // @todo: remove after https://cibotech.atlassian.net/browse/CPD-3767
    if (request.owner) {
      // @ts-ignore
      request.owner = `${request.owner}`
    }
    return mpAgent.put<MpxListResponse<{ resourceId: string }>>(PATHS.CONFIRM_IMPORT, request)
  }

  static stats(params: FieldsStatsQueryOptions) {
    return mpAgent.post<FieldFDRStatsResponse>(
      PATHS.STATS,
      FieldsAPI.transformFieldQueryParams(params ? params : {})
    )
  }

  static transformFieldQueryParams({
    owner,
    grower,
    programs = [],
    sort,
    prgGlobalSearchStatus,
    prgGlobalSearchStatusProgramId,
    ...passthroughParams
  }: FieldQueryOptions) {
    //@todo make this interface better :shrug:
    sort?.forEach(sort => {
      if (sort.field.startsWith('recommendationScore:')) {
        programs.push({ id: sort.field.split(':')[1] })
      }
    })

    return {
      ...passthroughParams,
      // worksaround until https://cibotech.atlassian.net/browse/CPD-3767
      owner: !!owner ? `${owner}` : undefined,
      grower: grower && grower !== '' ? grower : undefined,
      sort: FieldsAPI.transformQuerySortParam(sort),
      programs: programs?.length === 0 ? undefined : programs,
      prgGlobalSearchStatus: !!prgGlobalSearchStatus?.length
        ? prgGlobalSearchStatus.join(',')
        : undefined,
      prgGlobalSearchStatusProgramId,
      // paramsSerializer: p => queryString.stringify(p),
    }
  }

  static nameAvailable({
    excludeResourceId,
    name,
    owner,
  }: { excludeResourceId?: Field['resourceId'] } & Pick<FieldQueryOptions, 'name' | 'owner'>) {
    return mpAgent
      .get<PaginationResponse<Field>>(PATHS.FIELDS, {
        params: {
          fieldName: name,
          owner,
          limit: 1,
        },
        paramsSerializer: p => queryString.stringify(p),
      })
      .then(response =>
        // this query looks for any items with matching names and owners up to one item long.
        // if the query returns an item
        response.data?.numAvailable === 1
          ? // and we're editing an item
            !!excludeResourceId
            ? // the name IS available if the item we're editing is the item returned
              response.data.items[0].resourceId === excludeResourceId
            : // otherwise the name is NOT available
              false
          : // the name IS available if there are no query results
            response.data?.numAvailable === 0
      )
  }
}
